import React, { ChangeEvent, KeyboardEvent, useId, forwardRef, Ref } from 'react';
import clsx from 'clsx';
import styles from './TextField.module.scss';
import { Typographies, Typography } from '@/components/atoms/Typography';
import { ValidationItem } from '@/components/atoms/ValidationItem';
import { AnimateHeight } from '@/components/helpers/AnimateHeight';

export type TextFieldCustomProps = {
  value?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFocus?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onClick?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  label?: string;
  placeholder?: string;
  type?: string;
  error?: string;
  multiline?: boolean;
  rows?: number;
  maxLength?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  wrapperClassName?: React.ReactNode;
  inputClassName?: string;
};

type TextFieldRef = HTMLInputElement | HTMLTextAreaElement;

const TextField = forwardRef<TextFieldRef, TextFieldCustomProps>(
  (
    {
      value = '',
      onChange,
      onBlur,
      onFocus,
      onKeyDown,
      label,
      placeholder,
      type = 'text',
      error,
      multiline,
      rows = 3,
      maxLength,
      disabled,
      autoFocus,
      startAdornment,
      endAdornment,
      wrapperClassName,
      inputClassName,
    },
    ref,
  ) => {
    const inputId = useId();

    return (
      <div
        className={clsx(styles.wrapper, {
          [styles.error]: error,
          [styles.disabled]: disabled,
          wrapperClassName,
        })}
      >
        {label && (
          <label htmlFor={inputId} className={styles.label}>
            <Typography variant={Typographies.LABEL_2_SEMIBOLD}>{label}</Typography>
          </label>
        )}

        <div className={styles.input_wrapper}>
          {startAdornment && <div className={clsx(styles.adornment, styles['adornment--start'])}>{startAdornment}</div>}

          {multiline ? (
            <textarea
              ref={ref as Ref<HTMLTextAreaElement>}
              id={inputId}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              onFocus={onFocus}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              disabled={disabled}
              rows={rows}
              maxLength={maxLength}
              autoFocus={autoFocus}
              className={clsx(styles.input, inputClassName)}
            />
          ) : (
            <input
              ref={ref as Ref<HTMLInputElement>}
              id={inputId}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              onFocus={onFocus}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              disabled={disabled}
              type={type}
              maxLength={maxLength}
              autoFocus={autoFocus}
              className={clsx(styles.input, inputClassName)}
            />
          )}

          {endAdornment && <div className={clsx(styles.adornment, styles['adornment--end'])}>{endAdornment}</div>}
        </div>

        <AnimateHeight in={!!error}>
          <div className={styles.error}>
            <ValidationItem text={error || ''} type="error" />
          </div>
        </AnimateHeight>
      </div>
    );
  },
);

TextField.displayName = 'TextField';

export { TextField };
